// utils/calculatePayForEmployee.js
// Beräknar lön/tid för en anställd baserat på skift, OB, övertid, frånvaro, rast och semesterersättning.

const parseMinutes = (str) => {
  // Gör "HH:mm" -> minuter sedan midnatt
  const [h, m] = str.split(":").map(Number);
  return h * 60 + m;
};

const rangesOverlap = (start1, end1, start2, end2) => {
  // Kollar om två minut-intervall överlappar
  return Math.max(start1, start2) < Math.min(end1, end2);
};

/**
 * Normaliserar ett [from,to]-intervall till samma tidslinje som skiftet.
 * - Om intervallet korsar midnatt: to += 1440
 * - Om skiftet korsar midnatt och intervallet hamnar "före" skiftstart,
 *   flytta intervallet till nästa dygn (+1440).
 */
const normalizeForShift = (fromMin, toMin, sMin, eMin) => {
  let f = fromMin;
  let t = toMin;

  if (t <= f) t += 1440;          // intervallet korsar midnatt
  if (eMin > 1440 && t <= sMin) { // skift korsar midnatt och intervallet ligger före start
    f += 1440;
    t += 1440;
  }
  return [f, t];
};

export const calculatePayForEmployee = ({
  employee,
  shifts,
  payRates,
  vacationPay,
  overtimeRules,
  obRules,
  absenceRules,
  returnDetailed = false,

  // (valfritt) om man vill tvinga OB/semester på/av i en specifik körning
  obActivated,
  vacationActivated,
}) => {
  // Hämta vald lönetyp (Pay) för anställd
  const selectedPayObj = payRates.find(p => p._id === employee.pay) || null;

  // Läs lönetyp och belopp
  const payType = selectedPayObj?.payType || "hourly"; // "hourly" | "monthly"
  const monthlyAmount = Number(selectedPayObj?.amount || 0);

  /**
   * VIKTIGT:
   * - Timlön: amount = kr/h och används direkt.
   * - Månadslön: amount = kr/mån, men OT/frånvaro kräver tim-”ekvivalent”.
   *
   * För månadslön tar vi normtimmar:
   * - Om Pay har monthlyHoursNorm -> använd den.
   * - Annars fallback: räkna planerade timmar i månaden från shifts.
   * - Om det blir 0 -> använd 160h som sista fallback.
   */
  const DEFAULT_MONTHLY_HOURS_FALLBACK = 160;

  // Läs normtimmar satta på lönetypen (kan vara null)
  const payNormHours =
    payType === "monthly" && selectedPayObj?.monthlyHoursNorm
      ? Number(selectedPayObj.monthlyHoursNorm)
      : null;

  // Bestäm om OB är aktivt för denna person
  const resolvedObActivated =
    typeof obActivated === "boolean"
      ? obActivated
      : (employee?.payrollSettings?.includeOB ?? true);

  // Bestäm om semesterersättning är aktiv
  const resolvedVacationActivated =
    typeof vacationActivated === "boolean"
      ? vacationActivated
      : (employee?.payrollSettings?.includeVacation ?? true);

  // Räkna semesterersättningens procentsats
  const vacationRate =
    vacationPay?.vacationPayActive && resolvedVacationActivated
      ? vacationPay.vacationPayPercentage / 100
      : 0;

  // Samlingsvariabler för månads/period-summering
  let totalBreak = 0;          // totala rastminuter
  let totalOvertime = 0;       // totala övertidsminuter
  let overtimeBonus = 0;       // totala övertids-pengar
  let obTotal = 0;             // totala OB-pengar
  let absenceLoss = 0;         // totala frånvaravdrag
  let absenceComp = 0;         // totala frånvaroersättningar
  let totalAbsenceHours = 0;   // totala frånvarotimmar
  let totalWorkedMinutes = 0;  // arbetade minuter (utan OT inne i passet)

  // Rader per skift (för detailed view)
  const breakdownRows = [];

  // Mappar för uppdelning per regel (för UI)
  const obBreakdownMap = {};        // { [obId]: { _id, name, hours, pay } }
  const overtimeBreakdownMap = {};  // { [otId]: { _id, name, overtimeHours, pay } }
  const absenceBreakdownMap = {};   // { [absId]: { _id, name, hours, compPay, lossPay, percentage } }

  // Planerad rast i minuter (räknas alltid, oavsett frånvaro)
  const calculatePlannedBreakMinutes = (emp) => {
    if (!Array.isArray(emp.breaks)) return 0;
    let total = 0;
    emp.breaks.forEach(brk => {
      if (!brk.from || !brk.to) return;
      let from = parseMinutes(brk.from);
      let to = parseMinutes(brk.to);
      if (to <= from) to += 1440; // rast över midnatt
      total += to - from;
    });
    return total;
  };

  // Faktisk rast i minuter (tas bort om den överlappar frånvaro)
  const calculateBreakMinutes = (emp, absences) => {
    if (!Array.isArray(emp.breaks)) return 0;
    let total = 0;
    emp.breaks.forEach(brk => {
      if (!brk.from || !brk.to) return;
      let from = parseMinutes(brk.from);
      let to = parseMinutes(brk.to);
      if (to <= from) to += 1440;

      // Om rasten överlappar frånvaro ska den inte räknas
      const overlapsAbsence = (absences || []).some(abs => {
        if (abs.appliesWholeShift) return true;
        if (!abs.from || !abs.to) return false;
        let aFrom = parseMinutes(abs.from);
        let aTo = parseMinutes(abs.to);
        if (aTo <= aFrom) aTo += 1440;
        return rangesOverlap(from, to, aFrom, aTo);
      });

      if (!overlapsAbsence) total += to - from;
    });
    return total;
  };

  // Räknar frånvarotimmar i ett skift (normaliserat över midnatt)
  const calculateAbsenceHours = (absences, shiftStart, shiftEnd) => {
    let total = 0;
    let sMin = parseMinutes(shiftStart);
    let eMin = parseMinutes(shiftEnd);
    if (eMin <= sMin) eMin += 1440; // skift över midnatt

    if (!Array.isArray(absences)) return 0;

    absences.forEach(abs => {
      if (!abs.absenceType) return;

      // Hela passet frånvarande
      if (abs.appliesWholeShift) {
        total += (eMin - sMin) / 60;
        return;
      }

      if (!abs.from || !abs.to) return;

      let from = parseMinutes(abs.from);
      let to = parseMinutes(abs.to);

      const [aFrom, aTo] = normalizeForShift(from, to, sMin, eMin);

      const overlapStart = Math.max(sMin, aFrom);
      const overlapEnd = Math.min(eMin, aTo);
      const overlap = Math.max(0, overlapEnd - overlapStart);
      total += overlap / 60;
    });

    return total;
  };

  // Räknar övertidsminuter och delar upp i inne/utanför passet
  const calculateOvertimeHoursDetailed = (overtimeArr, shiftStartMin, shiftEndMin, absences, breaks = []) => {
    if (!Array.isArray(overtimeArr)) return { insideShiftMinutes: 0, outsideShiftMinutes: 0 };

    // Normalisera skiftet till en tidslinje
    let normShiftStart = shiftStartMin;
    let normShiftEnd = shiftEndMin;
    if (normShiftEnd <= normShiftStart) normShiftEnd += 1440;

    let insideShiftMinutes = 0;
    let outsideShiftMinutes = 0;

    overtimeArr.forEach(ot => {
      if (!ot.from || !ot.to) return;
      let fromMin = parseMinutes(ot.from);
      let toMin = parseMinutes(ot.to);
      if (toMin <= fromMin) toMin += 1440;

      // Om skiftet korsar midnatt och övertiden ligger tidigt, flytta övertiden till nästa dygn
      if (normShiftEnd > 1440 && fromMin < normShiftStart) {
        fromMin += 1440;
        toMin += 1440;
      }

      // Skippa övertid som överlappar frånvaro
      const overlapsAbsence = (absences || []).some(abs => {
        if (abs.appliesWholeShift) return true;
        if (!abs.from || !abs.to) return false;
        let aFrom = parseMinutes(abs.from);
        let aTo = parseMinutes(abs.to);
        const [naFrom, naTo] = normalizeForShift(aFrom, aTo, normShiftStart, normShiftEnd);
        return rangesOverlap(fromMin, toMin, naFrom, naTo);
      });
      if (overlapsAbsence) return;

      // Gå minut för minut och räkna vad som är inne/utanför skiftet
      for (let m = fromMin; m < toMin; m++) {
        const inBreak = (breaks || []).some(b => {
          if (!b.from || !b.to) return false;
          let bFrom = parseMinutes(b.from);
          let bTo = parseMinutes(b.to);
          if (bTo <= bFrom) bTo += 1440;
          return m >= bFrom && m < bTo;
        });

        const inAbsence = (absences || []).some(abs => {
          if (!abs.from || !abs.to) return false;
          let aFrom = parseMinutes(abs.from);
          let aTo = parseMinutes(abs.to);
          const [naFrom, naTo] = normalizeForShift(aFrom, aTo, normShiftStart, normShiftEnd);
          return m >= naFrom && m < naTo;
        });

        if (inBreak || inAbsence) continue;

        if (m >= normShiftStart && m < normShiftEnd) {
          insideShiftMinutes += 1;
        } else {
          outsideShiftMinutes += 1;
        }
      }
    });

    return { insideShiftMinutes, outsideShiftMinutes };
  };

  // ----------------------------
  // 1) Räkna planerade timmar i månaden (endast om månadslön och ingen norm finns)
  // ----------------------------
  let plannedMinutesMonth = 0;

  if (payType === "monthly" && !payNormHours) {
    shifts.forEach(shift => {
      const emp = shift.employees.find(e => e.employeeId === employee._id);
      if (!emp) return;
      if (!shift?.startTime || !shift?.endTime) return;

      const sMin = parseMinutes(shift.startTime);
      let eMin = parseMinutes(shift.endTime);
      if (eMin <= sMin) eMin += 1440;

      const grossMinutes = eMin - sMin;
      const plannedBreakMin = calculatePlannedBreakMinutes(emp);

      const plannedWorkMin = Math.max(grossMinutes - plannedBreakMin, 0);
      plannedMinutesMonth += plannedWorkMin;
    });
  }

  const plannedHoursMonth =
    payType === "monthly" && !payNormHours
      ? (plannedMinutesMonth / 60)
      : 0;

  // ----------------------------
  // 2) Bestäm tim-ekvivalent för månadslön
  // ----------------------------
  const monthlyHourlyEq =
    payType === "monthly"
      ? (
          // a) använd normtimmar om de finns på Pay
          payNormHours
            ? (monthlyAmount / payNormHours)
            // b) annars använd planerade timmar från skiften
            : (plannedHoursMonth > 0
                ? (monthlyAmount / plannedHoursMonth)
                // c) sista fallback om inga kompletta skift finns
                : (monthlyAmount / DEFAULT_MONTHLY_HOURS_FALLBACK))
        )
      : monthlyAmount;

  // payRate är alltid "kr per timme" (timlön eller månadslön-ekvivalent)
  const payRate = monthlyHourlyEq;

  // ----------------------------
  // 3) Gå igenom varje skift och bygg upp totals + breakdown
  // ----------------------------
  shifts.forEach(shift => {
    const emp = shift.employees.find(e => e.employeeId === employee._id);
    if (!emp) return;

    const sMin = parseMinutes(shift.startTime);
    let eMin = parseMinutes(shift.endTime);
    if (eMin <= sMin) eMin += 1440;

    // Övertid uppdelad i inside/outside
    const { insideShiftMinutes, outsideShiftMinutes } = calculateOvertimeHoursDetailed(
      emp.overtime,
      sMin,
      eMin,
      emp.absences,
      emp.breaks
    );

    const grossMinutes = eMin - sMin;

    // Rast (faktisk rast som inte överlappar frånvaro)
    const breakMinutes = calculateBreakMinutes(emp, emp.absences);
    totalBreak += breakMinutes;

    // Frånvaro
    const absenceHours = calculateAbsenceHours(emp.absences, shift.startTime, shift.endTime);
    totalAbsenceHours += absenceHours;

    // Övertidsminuter
    const shiftOvertimeMinutes = insideShiftMinutes + outsideShiftMinutes;
    totalOvertime += shiftOvertimeMinutes;

    // Arbetade minuter utan dubbelräkning av OT inne i passet + utan rast
    const nonOverlappingWorkedMinutes = grossMinutes - insideShiftMinutes - breakMinutes;
    totalWorkedMinutes += Math.max(nonOverlappingWorkedMinutes, 0);

    // ----- ÖVERTIDSPENGAR per regel -----
    let shiftOvertimeBonus = 0;
    const overtimeRanges = [];

    if (Array.isArray(emp.overtime)) {
      emp.overtime.forEach(ot => {
        const rule = overtimeRules.find(r => r._id === ot.type);
        if (!rule) return;

        const from = parseMinutes(ot.from);
        const to = parseMinutes(ot.to) + (parseMinutes(ot.to) <= from ? 1440 : 0);

        // Skippa OT som överlappar frånvaro
        const overlapsAbsence = (emp.absences || []).some(abs => {
          if (abs.appliesWholeShift) return true;
          if (!abs.from || !abs.to) return false;
          let aFrom = parseMinutes(abs.from);
          let aTo = parseMinutes(abs.to);
          const [naFrom, naTo] = normalizeForShift(aFrom, aTo, sMin, eMin);
          return rangesOverlap(from, to, naFrom, naTo);
        });
        if (overlapsAbsence) return;

        const overMin = to - from;
        const worth = (overMin / 60) * payRate * (1 + (rule.percentage / 100));

        shiftOvertimeBonus += worth;
        overtimeBonus += worth;
        overtimeRanges.push(`${ot.from} - ${ot.to}`);

        // Spara breakdown per regel
        const rid = rule._id.toString();
        if (!overtimeBreakdownMap[rid]) {
          overtimeBreakdownMap[rid] = {
            _id: rid,
            name: rule.name,
            overtimeHours: 0,
            pay: 0,
            percentage: rule.percentage,
          };
        }
        overtimeBreakdownMap[rid].overtimeHours += overMin / 60;
        overtimeBreakdownMap[rid].pay += worth;
      });
    }

    // ----- OB -----
    let shiftObPay = 0;
    const obDetails = {};
    const obDetailsPay = {};

    if (Array.isArray(emp.obDetails)) {
      emp.obDetails.forEach(ob => {
        const rule = obRules.find(r => r._id === ob.obType);
        if (!rule) return;

        const hours = ob.obHours || 0;

        const value = Number(ob.value ?? rule.value ?? rule.additionalPay ?? 0);
        const type = ob.type ?? rule.type ?? "fixed";

        let pay = 0;

        if (resolvedObActivated) {
          if (type === "percentage") {
            pay = hours * payRate * (value / 100);
          } else {
            pay = hours * value;
          }
        }

        if (resolvedObActivated) {
          obTotal += pay;
          shiftObPay += pay;
        }

        obDetails[ob.obType.toString()] = hours;
        obDetailsPay[ob.obType.toString()] = pay;

        // Breakdown per OB-regel
        const rid = rule._id.toString();
        if (!obBreakdownMap[rid]) {
          obBreakdownMap[rid] = { _id: rid, name: rule.name, hours: 0, pay: 0 };
        }
        obBreakdownMap[rid].hours += hours;
        obBreakdownMap[rid].pay += pay;
      });
    }

    // ----- FRÅNVARO (ersättning + avdrag) -----
    let shiftAbsenceComp = 0;

    if (Array.isArray(emp.absences)) {
      emp.absences.forEach(abs => {
        if (!abs.absenceType) return;

        const rule = absenceRules.find(a => a._id === abs.absenceType);
        if (!rule) return;

        // Clamp procent 0–100
        const percentage = Math.min(Math.max(rule.percentage, 0), 100);

        // Ersättning per timme för frånvaron
        const hourlyComp = payRate * (percentage / 100);

        // Frånvarotimmar för just denna rad
        const absHours = abs.appliesWholeShift
          ? (grossMinutes / 60)
          : calculateAbsenceHours([abs], shift.startTime, shift.endTime);

        // Betalt och avdrag
        const compPay = absHours * hourlyComp;
        const lossPay = absHours * (payRate * (1 - percentage / 100));

        shiftAbsenceComp += compPay;
        absenceLoss += lossPay;

        // Breakdown per frånvaroregler
        const rid = rule._id.toString();
        if (!absenceBreakdownMap[rid]) {
          absenceBreakdownMap[rid] = {
            _id: rid,
            name: rule.name,
            hours: 0,
            compPay: 0,
            lossPay: 0,
            percentage,
          };
        }
        absenceBreakdownMap[rid].hours += absHours;
        absenceBreakdownMap[rid].compPay += compPay;
        absenceBreakdownMap[rid].lossPay += lossPay;
      });
    }

    absenceComp += shiftAbsenceComp;

    // Spara detaljerad rad (om UI vill visa per dag)
    breakdownRows.push({
      date: shift.date,
      shiftId: shift._id?.toString?.(),
      obDetails,
      obDetailsPay,
      overtimeRanges,
      shiftOvertimeBonus,
      shiftObPay,
      shiftAbsenceComp,
      baseMinutes: Math.max(nonOverlappingWorkedMinutes, 0),
      overtimeMinutes: shiftOvertimeMinutes,
      breakMinutes,
      absenceHours,
    });
  });

  // ----------------------------
  // 4) Slut-summeringar för perioden
  // ----------------------------
  const workedHoursExclOvertime = totalWorkedMinutes / 60;
  const baseHours = totalWorkedMinutes / 60;

  // Grundlön:
  // - Timlön: arbetade timmar * timlön
  // - Månadslön: fast belopp (justeras sen av OT/OB/frånvaro)
  const baseSalary =
    payType === "monthly"
      ? monthlyAmount
      : (workedHoursExclOvertime * payRate);

  // Totaltid = arbetad tid + övertid - frånvaro
  const totalHours =
    workedHoursExclOvertime + ((totalOvertime / 60) - totalAbsenceHours);

  // Lön innan semesterersättning
  const beforeVac = baseSalary + obTotal + overtimeBonus - absenceLoss;

  // Semesterersättning (procent på beforeVac)
  const vacComp = vacationRate > 0 ? beforeVac * vacationRate : 0;

  const totalBeforeTax = beforeVac + vacComp;

  // ----------------------------
  // 5) Sammanställning för UI
  // ----------------------------
  const summary = {
    totalHours,
    baseHours: (baseHours - totalAbsenceHours).toFixed(2),
    overtimeHours: totalOvertime / 60,
    absenceHours: totalAbsenceHours,

    // Visar tim-equivalent som UI kan använda
    hourlyRate: payRate.toFixed(2),
    payType,                        // bra för UI/debug
    monthlyBase: monthlyAmount,     // visar månadslönens grund

    // Debug-info för månadslön:
    plannedHoursMonth:
      payType === "monthly" && !payNormHours ? Number(plannedHoursMonth || 0) : undefined,
    monthlyHoursNorm:
      payType === "monthly" ? (payNormHours || null) : undefined,

    obPay: obTotal.toFixed(2),
    overTimePay: overtimeBonus.toFixed(2),
    vacationCompensation: vacComp.toFixed(2),
    absenceComp: absenceComp.toFixed(2),

    totalPay: totalBeforeTax.toFixed(2),

    obBreakdown: Object.values(obBreakdownMap).map(x => ({
      ...x,
      hours: Number(x.hours || 0),
      pay: Number(x.pay || 0),
    })),
    overtimeBreakdown: Object.values(overtimeBreakdownMap).map(x => ({
      ...x,
      overtimeHours: Number(x.overtimeHours || 0),
      pay: Number(x.pay || 0),
    })),
    absenceBreakdown: Object.values(absenceBreakdownMap).map(x => ({
      ...x,
      hours: Number(x.hours || 0),
      compPay: Number(x.compPay || 0),
      lossPay: Number(x.lossPay || 0),
      pay: Number(x.compPay || 0),
    })),
  };

  // Returnera detailed eller bara summary beroende på flagga
  return returnDetailed ? { summary, breakdownRows } : summary;
};